/* eslint-disable react/prop-types */
/**
 * Today's MOTs Queue
 * ==================
 * Dedicated landing screen for the MOT tester role.
 *
 * Route: /app/tech/mot
 *
 * Design principle (spec): "A focused MOT operational environment."
 * Ruthlessly minimal. Big key-tag badges. Glove-friendly. Glance-readable
 * status chips. No nested menus. One primary action per row.
 *
 * Source of truth: `intake_sessions` filtered by `mot_slot_time == today`
 * (served by `GET /api/mcc/today`).
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  RefreshCw,
  Wrench,
  AlertTriangle,
  Clock,
  CheckCircle2,
  LogOut,
  Trash2,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-GB", {
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/**
 * Slot-time prominence rules (tester-led):
 *   • Past slot   → rose (overdue)
 *   • Within 30m  → emerald (next up)
 *   • Future      → slate (on the board, not yet up)
 *   • Missing     → amber (FOH needs to set)
 *
 * Returns a stable comparator key so the queue is naturally ordered:
 *   chronological for slotted rows, "no slot" rows pinned to the bottom.
 */
const slotMillis = (iso) => {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
};

const slotTone = (iso) => {
  if (!iso)
    return {
      cls: "bg-amber-500/15 ring-1 ring-amber-500/50 text-amber-200",
      label: "—:—",
      sub: "no slot",
    };
  const now = Date.now();
  const t = new Date(iso).getTime();
  const diffMin = (t - now) / 60000;
  const label = fmtTime(iso);
  if (diffMin < -10) {
    return {
      cls: "bg-rose-500/15 ring-1 ring-rose-500/60 text-rose-200",
      label,
      sub: "overdue",
    };
  }
  if (diffMin < 30) {
    return {
      cls: "bg-emerald-500/20 ring-2 ring-emerald-400 text-emerald-100 shadow-lg shadow-emerald-500/20",
      label,
      sub: diffMin < 0 ? "now" : "next up",
    };
  }
  return {
    cls: "bg-slate-700 ring-1 ring-slate-600 text-slate-100",
    label,
    sub: "scheduled",
  };
};

const visitTypePill = (visit_type, is_mot_only) => {
  if (visit_type === "service" || visit_type === "mot_service") {
    return { label: "MOT + SERVICE", cls: "bg-red-100 text-red-700 ring-1 ring-red-300" };
  }
  if (is_mot_only || visit_type === "mot") {
    return { label: "MOT ONLY", cls: "bg-teal-100 text-teal-800" };
  }
  if (visit_type === "diagnostic") {
    return { label: "DIAG", cls: "bg-purple-100 text-purple-700" };
  }
  return { label: "OTHER", cls: "bg-slate-100 text-slate-600" };
};

const statusChip = (row) => {
  const wf = row.mot_workflow_status || "";
  const tr = row.mot_test_result || "";
  if (tr === "FAILED" && row.mcc_summary?.level4_pending > 0) {
    return { label: "MCC REQUIRED", cls: "bg-red-600 text-white animate-pulse" };
  }
  if (tr === "PASSED") {
    return { label: "MOT PASSED", cls: "bg-emerald-600 text-white" };
  }
  if (tr === "PASSED_WITH_DEFECTS" || tr === "PRS") {
    return { label: "PASS W/ ADVISORIES", cls: "bg-amber-500 text-white" };
  }
  if (tr === "FAILED") {
    return { label: "MOT FAILED", cls: "bg-red-500 text-white" };
  }
  if (wf === "in_progress" || wf === "tester_started") {
    return { label: "IN PROGRESS", cls: "bg-indigo-500 text-white" };
  }
  if (wf === "pricing_ready" || wf === "result_imported") {
    return { label: "READY FOR FOH", cls: "bg-slate-700 text-white" };
  }
  return { label: "AWAITING MOT", cls: "bg-slate-300 text-slate-800" };
};

function KeyBadge({ value, onSite }) {
  // Operationally critical — testers identify cars by their key tag. Big,
  // colour-coded, persistent. Visible without opening anything.
  if (!value) {
    return (
      <span className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-slate-200 text-slate-400 text-[10px] font-semibold">
        NO KEY
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center justify-center w-14 h-14 rounded-lg border-2 font-black text-2xl tracking-tight ${
        onSite
          ? "bg-emerald-50 text-emerald-700 border-emerald-400"
          : "bg-red-50 text-red-700 border-red-400"
      }`}
      title={onSite ? "Vehicle on site" : "Key tag — vehicle NOT yet on site"}
      data-testid={`mot-key-tag-${value}`}
    >
      {value}
    </span>
  );
}

export default function MOTTodaysQueuePage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");
  // Iter180b — per-row archive/delete state. ``confirmRow`` holds the
  // row pending confirmation; ``deletingId`` blocks the action button
  // mid-network so glove-thumbs don't double-fire.
  const [confirmRow, setConfirmRow] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/mcc/today`);
      setRows(r.data?.items || []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load today's MOTs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh every 30 s so testers always see live state without
    // having to think about pulling.
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  // Iter180b — Archive (delete) a row from the tester queue. Reuses the
  // existing FOH archive endpoint which marks ``intake_sessions.archived
  // = true``. Optimistic remove from the list; if the request fails we
  // surface a banner and reload to recover real state.
  const handleDelete = useCallback(async (row) => {
    if (!row?.session_id) return;
    setDeletingId(row.session_id);
    setErr("");
    try {
      await axios.post(
        `${API}/foh/mot-session/${row.session_id}/archive`,
        { reason: "tester_removed_from_queue" },
      );
      setRows((prev) => prev.filter((r) => r.session_id !== row.session_id));
      setConfirmRow(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to remove from queue");
      // Re-pull to recover canonical state if the optimistic remove
      // diverged from the server.
      load();
    } finally {
      setDeletingId(null);
    }
  }, [load]);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100" data-testid="mot-todays-queue">
      {/* Header — kept ruthlessly minimal */}
      <header className="border-b border-slate-700 bg-slate-950 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Wrench className="w-6 h-6 text-teal-400" />
            <div>
              <h1 className="text-lg font-bold tracking-tight">Today&apos;s MOTs</h1>
              <p className="text-[11px] text-slate-400">
                {new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
                {user?.name ? ` · ${user.name}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-300 hover:text-white hover:bg-slate-800"
              onClick={() => { setRefreshing(true); load(); }}
              data-testid="mot-queue-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-slate-300 hover:text-white hover:bg-slate-800"
              onClick={logout}
              data-testid="mot-queue-logout"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 space-y-3">
        {loading && (
          <div className="text-center py-12 text-slate-400 text-sm">Loading…</div>
        )}
        {err && (
          <div className="bg-red-900/30 border border-red-700 text-red-200 px-3 py-2 rounded text-sm">
            {err}
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="text-center py-16 text-slate-500">
            <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No MOTs scheduled today.</p>
          </div>
        )}

        {rows
          .slice()
          .sort((a, b) => slotMillis(a.mot_slot_time) - slotMillis(b.mot_slot_time))
          .map((row) => {
          const v = visitTypePill(row.visit_type, row.is_mot_only);
          const s = statusChip(row);
          const mcc4 = row.mcc_summary?.level4_pending || 0;
          const mccPending = row.mcc_summary?.pending || 0;
          const mccComplete = row.mcc_summary?.complete || 0;
          const mccTotal = row.mcc_summary?.total || 0;
          return (
            <Card
              key={row.session_id}
              className="bg-slate-800 border-slate-700 text-slate-100 p-3"
              data-testid={`mot-row-${row.reg}`}
            >
              <div className="flex items-center gap-4">
                {/* Slot-time badge — large, glance-readable, drives queue order */}
                {(() => {
                  const tone = slotTone(row.mot_slot_time);
                  return (
                    <div
                      className={`flex-shrink-0 flex flex-col items-center justify-center rounded-lg px-3 py-2 min-w-[80px] ${tone.cls}`}
                      data-testid={`mot-row-slot-${row.reg}`}
                      title={
                        row.mot_slot_time
                          ? new Date(row.mot_slot_time).toLocaleString("en-GB")
                          : "No slot set — ask FOH to add one."
                      }
                    >
                      <span className="text-[22px] font-black tracking-tight leading-none font-mono">
                        {tone.label}
                      </span>
                      <span className="text-[9.5px] uppercase tracking-[0.18em] font-semibold opacity-80 mt-0.5">
                        {tone.sub}
                      </span>
                    </div>
                  );
                })()}
                <KeyBadge value={row.key_tag_number} onSite={row.vehicle_on_site} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-2xl font-black tracking-tight text-white">
                      {row.reg || "—"}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${v.cls}`}>
                      {v.label}
                    </span>
                    {row.customer_waiting && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-400 text-amber-900 animate-pulse">
                        WAITING
                      </span>
                    )}
                    {/* Iter180 — FOH bounced the job back to MCC. */}
                    {row.mcc_summary?.bounce_back_pending && (
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-500 text-white ring-1 ring-purple-300"
                        data-testid={`mot-row-bounce-back-${row.reg}`}
                        title="FOH sent this job back to complete MCC"
                      >
                        INCOMING FROM FOH
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5 truncate">
                    {row.make || ""} {row.model || ""}
                    {row.customer_name ? ` · ${row.customer_name}` : ""}
                  </div>
                  {/* Slot-warning pill — surfaces sessions that have
                      "slipped in" via the inclusivity rule (past slot,
                      no slot at all). Tells the tester at a glance that
                      this isn't a clean today-slot row and FOH may
                      need to fix the slot. */}
                  {row.slot_warning && (
                    <div className="mt-1">
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-900/50 text-amber-200 ring-1 ring-amber-700"
                        data-testid={`mot-row-slot-warning-${row.reg}`}
                        title={
                          row.slot_warning === "slot_in_past"
                            ? "Slot date is in the past — ask FOH to update if this is still today's MOT."
                            : row.slot_warning === "slot_in_future"
                            ? "Slot date is in the future."
                            : "No slot time set — ask FOH to add one."
                        }
                      >
                        <AlertTriangle className="w-3 h-3" />
                        {row.slot_warning === "slot_in_past" && "Slot in past"}
                        {row.slot_warning === "slot_in_future" && "Slot in future"}
                        {row.slot_warning === "slot_missing" && "No slot set"}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className={`px-2 py-1 rounded text-[11px] font-bold ${s.cls}`}>
                      {s.label}
                    </span>
                    {mccTotal > 0 && (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold ${
                          mcc4 > 0
                            ? "bg-red-900/50 text-red-200 ring-1 ring-red-700"
                            : mccPending > 0
                            ? "bg-amber-900/40 text-amber-200 ring-1 ring-amber-700"
                            : "bg-emerald-900/40 text-emerald-200 ring-1 ring-emerald-700"
                        }`}
                        data-testid={`mot-row-mcc-${row.reg}`}
                      >
                        {mcc4 > 0 && <AlertTriangle className="w-3 h-3" />}
                        {mccPending === 0 ? (
                          <CheckCircle2 className="w-3 h-3" />
                        ) : null}
                        MCC {mccComplete}/{mccTotal}
                        {mcc4 > 0 ? ` · ${mcc4} required` : ""}
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-slate-400 hover:text-red-300 hover:bg-red-900/30 h-12 w-12"
                    onClick={() => setConfirmRow(row)}
                    title="Remove from today's queue"
                    aria-label="Remove from today's queue"
                    data-testid={`mot-row-delete-${row.reg}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                  <Button
                    className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-bold h-12 px-4 text-sm"
                    onClick={() => navigate(`/app/tech/mot/${row.job_id}`)}
                    disabled={!row.job_id}
                    data-testid={`mot-row-open-${row.reg}`}
                  >
                    {row.mot_test_result ? "Continue" : "Start MOT"}
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </main>

      {/* Iter180b — confirm-before-remove dialog. Archive is reversible
          server-side (the session simply gets archived=true) so we don't
          need destructive-red language, but we still confirm to prevent
          glove-fat-finger removals. */}
      <AlertDialog
        open={!!confirmRow}
        onOpenChange={(v) => (v ? null : setConfirmRow(null))}
      >
        <AlertDialogContent
          className="bg-slate-900 border-slate-700 text-slate-100"
          data-testid="mot-row-delete-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              Remove from today&apos;s queue?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-300">
              {confirmRow?.reg ? (
                <>
                  <span className="font-bold text-white">{confirmRow.reg}</span>{" "}
                  will disappear from your Today&apos;s MOTs list. FOH can
                  restore it from the dashboard if needed. The job itself
                  isn&apos;t deleted.
                </>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700"
              data-testid="mot-row-delete-cancel"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-500 text-white"
              onClick={() => handleDelete(confirmRow)}
              disabled={deletingId === confirmRow?.session_id}
              data-testid="mot-row-delete-confirm"
            >
              {deletingId === confirmRow?.session_id ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Removing…</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-1.5" />Remove</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
